import { useContext } from "react"
import { DeliveryRouteService, InvoicesResponse } from "../../../../../utilities/services/DeliveryRouteService"
import { StoreContext } from "../../../../../utilities/contexts/StoreContext"
import { LanguageContext } from "../../../../../utilities/contexts/LanguageContext"
import language from "../../../../../language/language"
import { SnackbarContext } from "../../../../../utilities/contexts/SnackbarContext"
import { AutoDispatchDetain } from "../../../../../utilities/types/DeliveryRouteTypes"

type BulkInvoices = {
    invoiceNumber: string
    invoiceDateTime: number
}

type StopAutoDispatchProps = {
    selectedReason: string
    setIsGridUpdating: (value: boolean) => void
    bulkInvoices: BulkInvoices[]
    setIsDisabled: (value: boolean) => void
    clearSelectedInvoiceData: () => void
    fetchInvoices: () => void
}

type StartAutoDispatchProps = {
    selectedInvoiceData: InvoicesResponse | null
    clearSelectedInvoiceData: () => void
    fetchInvoices: () => void
}

type OnHoldProps = {
    setIsGridUpdating: (value: boolean) => void
    bulkInvoices: BulkInvoices[]
    selectedInvoiceData: InvoicesResponse | null
    setOpenInvoiceActions: (value: boolean) => void
    setInvoiceDetails: (value: any) => void
    setIsDisabled: (value: boolean) => void
    clearSelectedInvoiceData: () => void
}

export const useHandleInvoiceButtons = () => {
    const { storeAddress, currentStore, isCompanyOwned } = useContext(StoreContext)
    const { currentLanguage } = useContext(LanguageContext)
    const { addSnack } = useContext(SnackbarContext)

    const stopAutoDispatch = async ({ selectedReason, setIsGridUpdating, bulkInvoices, setIsDisabled, clearSelectedInvoiceData, fetchInvoices }: StopAutoDispatchProps): Promise<void> => {
        try {
            setIsGridUpdating(true)
            const request = {
                invoiceNumberDateTimes: bulkInvoices,
                nssaInvoiceStatus: 'INVOICED',
                reason: selectedReason,
                stopAutodispatch: true
            }
            const response = await DeliveryRouteService.updateInvoiceStatus(request, currentStore)
            const successMessage = `${language[currentLanguage].stopAutoDispatched} ${bulkInvoices.map(invoice => invoice.invoiceNumber)}`
            if (response.status === 'Success') {
                addSnack({
                    severity: 'success',
                    message: successMessage,
                    action: null,
                    duration: 3000
                })
            } else {
                addSnack({
                    severity: 'warning',
                    message: response.message,
                    action: null,
                    duration: 3000
                })
            }
            setIsDisabled(true)
            clearSelectedInvoiceData()
            fetchInvoices()
        } catch (error: any) {
            setIsGridUpdating(false)
            addSnack({
                severity: 'error',
                message: language[currentLanguage].stopAutoDispatchedError,
                action: null,
                duration: 3000
            })
        }
    }

    const startAutoDispatch = async ({ selectedInvoiceData, clearSelectedInvoiceData, fetchInvoices }: StartAutoDispatchProps): Promise<void> => {
        const customerAddress = selectedInvoiceData?.deliveryAddress
        const request: AutoDispatchDetain = {
            storeNumber: currentStore,
            invoiceNumber: selectedInvoiceData?.invoiceNumber ?? '',
            invoiceDateTime: selectedInvoiceData?.invoiceDateTime?.toString() ?? '',
            pickupInstructions: '',
            dropoffInstructions: '',
            pickupAddress: `${storeAddress.addressLine1} ${storeAddress.addressLine2} ${storeAddress.city}, ${storeAddress.state} ${storeAddress.zipCode}`,
            dropoffAddress: `${customerAddress?.addressLine1} ${!!customerAddress?.addressLine2 ? customerAddress.addressLine2 : ''} ${customerAddress?.city}, ${customerAddress?.state} ${customerAddress?.zipCode}`,
            dropoffPhoneNumber: `${customerAddress?.phoneNumber}`,
            orderValueInCents: '0',
            dropoffBusinessName: customerAddress?.name || '',
            isCOS: isCompanyOwned
        }
        const successMessage = `${language[currentLanguage].autoDispatched
            .split('#id')
            .join(selectedInvoiceData?.invoiceNumber)}`
        try {
            await DeliveryRouteService.createRouteAndDelivery(request)
            addSnack({
                severity: 'success',
                message: successMessage,
                action: null,
                duration: 3000
            })
            clearSelectedInvoiceData()
            fetchInvoices()
        } catch (error) {
            addSnack({
                severity: 'error',
                message: language[currentLanguage].autoDispatchedError,
                action: null,
                duration: 3000
            })
        }
    }

    const handleOnHold = async ({ setIsGridUpdating, bulkInvoices, selectedInvoiceData, setOpenInvoiceActions, setInvoiceDetails, setIsDisabled, clearSelectedInvoiceData }: OnHoldProps) => {
        const request = {
            invoiceNumberDateTimes: bulkInvoices,
            nssaInvoiceStatus: selectedInvoiceData?.nssaInvoiceStatus === 'ON_HOLD' ? 'INVOICED' : 'ON_HOLD',
            stopAutodispatch: false
        }
        try {
            const response = await DeliveryRouteService.updateInvoiceStatus(
                request,
                currentStore
            )
            setOpenInvoiceActions(false)
            setInvoiceDetails(null)
            setIsDisabled(true)
            clearSelectedInvoiceData()
            if (response.status === 'Success') {
                addSnack({
                    severity: 'success',
                    message: `${language[currentLanguage].invoiceNumber}${bulkInvoices.map(invoice => invoice.invoiceNumber)} ${language[currentLanguage].hasUpdated}`,
                    action: null,
                    duration: 3000
                })
            } else {
                addSnack({
                    severity: 'warning',
                    message: response.message,
                    action: null,
                    duration: 3000
                })
            }
        } catch (error) {
            addSnack({
                severity: 'error',
                message: language[currentLanguage].errorMessage,
                action: null,
                duration: 3000
            })
        }
    }

    return { stopAutoDispatch, startAutoDispatch, handleOnHold }
}